import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Eye, History } from "lucide-react";

import { HistoryDrawer } from "@/components/HistoryDrawer";
import { Button } from "@/components/ui/button";
import { ActionPlansPage } from "@/features/action-plans";
import { QuestionnairePage } from "@/features/questionnaire";
import { useMemberHistory } from "@/features/questionnaire/api";
import { setOnBehalfOf,
  setOnBehalfReadOnly } from "@/lib/api/on-behalf";
import { ReadOnlyProvider } from "@/lib/read-only";
import { cn } from "@/lib/utils/cn";

import { useFormationGroupQuery } from "../api";

type ParticipantTab = "assessment" | "actionPlan";

/**
 * A participant's own data, read by an administrator.
 *
 * The same mechanism the partner surface uses — `setOnBehalfOf` retargets the
 * existing questionnaire and action-plan pages at the participant, whose API
 * calls then carry `?on_behalf_of`. Two differences from `MemberDataView`, both
 * deliberate:
 *
 * * no consent query. An administrator is not subject to consent, so there is
 *   nothing to wait for and nothing to gate on;
 * * always read-only. The ticket's verb for the assessment, the results and the
 *   plan is « consulter » — only the enterprise profile is « corriger » — which
 *   is also what the Django participant row links to.
 *
 * The four destinations the Django row offers are all here: the assessment (the
 * results are a tab inside it), the action plan, the history drawer, and the
 * enterprise profile, which is its own screen because it is the editable one.
 */
export function ParticipantDataView({
  groupId,
  profileId,
}: {
  groupId: string;
  profileId: string;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<ParticipantTab>("assessment");
  const [historyOpen, setHistoryOpen] = useState(false);
  // Only fetched once opened; the on-behalf context is already set below.
  const history = useMemberHistory(historyOpen);
  const group = useFormationGroupQuery(groupId);
  const participant = (group.data?.participants ?? []).find(
    (row) => row.enterprise_profile_id === profileId,
  );

  // Set synchronously so the child pages' query keys (computed during their
  // render) already target the participant; cleared on unmount.
  setOnBehalfOf(profileId);
  setOnBehalfReadOnly(true);
  useEffect(() => {
    setOnBehalfOf(profileId);
    setOnBehalfReadOnly(true);
    return () => {
      setOnBehalfOf(null);
      setOnBehalfReadOnly(false);
    };
  }, [profileId]);

  const tabs: ParticipantTab[] = ["assessment", "actionPlan"];

  return (
    <div className="space-y-6">
      <div className="rounded-md border border-primary/30 bg-primary/5 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Eye className="size-4 text-primary" aria-hidden="true" />
            {participant
              ? t("formationGroups.detail.viewing", { name: participant.name })
              : t("formationGroups.detail.participants")}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHistoryOpen(true)}
            >
              <History className="mr-1.5 size-4" aria-hidden="true" />
              {t("formationGroups.detail.history")}
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link
                to="/formation-groups/$id/participants/$profileId/profile"
                params={{ id: groupId, profileId }}
              >
                {t("formationGroups.detail.profile")}
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/formation-groups/$id" params={{ id: groupId }}>
                <ArrowLeft className="mr-1.5 size-4" aria-hidden="true" />
                {t("formationGroups.back")}
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <div
        role="tablist"
        aria-label={t("formationGroups.detail.data")}
        className="inline-flex rounded-md border border-input p-0.5"
      >
        {tabs.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            onClick={() => setTab(name)}
            className={cn(
              "min-h-touch rounded px-3 py-1.5 text-sm font-medium transition-colors",
              tab === name
                ? "bg-primary text-primary-foreground"
                : "hover:bg-accent hover:text-accent-foreground",
            )}
          >
            {t(`formationGroups.detail.${name}`)}
          </button>
        ))}
      </div>

      {historyOpen && (
        <HistoryDrawer
          title={participant?.name ?? ""}
          onClose={() => setHistoryOpen(false)}
          query={history}
        />
      )}

      <ReadOnlyProvider value>
        {tab === "assessment" ? <QuestionnairePage /> : <ActionPlansPage />}
      </ReadOnlyProvider>
    </div>
  );
}
